import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import type { ReactNode, PointerEvent as ReactPointerEvent } from 'react';
import { createPortal } from 'react-dom';
import { useReducedMotion } from '@/hooks';
import { springTo } from '@/lib/spring';

/** A drag that moves less than this is a tap, so the handle still works as a button. */
const TAP_SLOP_PX = 6;
/** Let go past half the sheet's height — or this far, on a short sheet — and it dismisses. */
const MIN_DISMISS_PX = 120;
/** Fling speed is capped, so bunched-up pointer events can't throw the sheet into orbit. */
const MAX_FLING_PX_PER_S = 4000;

/** Where momentum would carry a flick (Apple's projection, deceleration rate 0.998). */
const project = (velocity: number) => ((velocity / 1000) * 0.998) / (1 - 0.998);
/** Resistance past the top edge: the sheet follows less the further it's pulled. */
const rubberBand = (offset: number, dimension: number) => (offset * dimension * 0.55) / (dimension + 0.55 * Math.abs(offset));

/**
 * The phone form of a popover: a bottom sheet the finger drives directly. It rises on a
 * spring; its handle and header drag it 1:1 (with a rubber band above the top); letting go
 * dismisses it when it's past halfway or flicked down — judged from where momentum would carry
 * it — and otherwise springs back carrying the finger's speed. It can be caught mid-flight. With
 * reduced motion it simply appears and disappears.
 */
export function BottomSheet({ label, onClose, children }: { label: string; onClose: () => void; children: (close: () => void) => ReactNode }) {
  const reduced = useReducedMotion();
  const sheetRef = useRef<HTMLDivElement>(null);
  const scrimRef = useRef<HTMLDivElement>(null);
  const y = useRef(0);
  const animation = useRef<{ stop: () => { value: number; velocity: number } } | null>(null);
  const drag = useRef<{ grab: number; start: number; history: [number, number][]; moved: boolean } | null>(null);
  const draggedSinceTap = useRef(false);
  const closing = useRef(false);

  const height = () => sheetRef.current?.offsetHeight ?? 0;
  const setY = useCallback((next: number) => {
    y.current = next;
    if (sheetRef.current) sheetRef.current.style.transform = `translateY(${next}px)`;
    const h = sheetRef.current?.offsetHeight;
    if (scrimRef.current && h) scrimRef.current.style.opacity = String(Math.max(0, Math.min(1, 1 - next / h)));
  }, []);
  const stopAnimation = () => {
    animation.current?.stop();
    animation.current = null;
  };

  const close = useCallback(
    (velocity = 0) => {
      if (closing.current) return;
      closing.current = true;
      animation.current?.stop();
      if (reduced) return onClose();
      animation.current = springTo({ from: y.current, to: height() + 40, velocity, response: 0.3, set: setY, done: onClose });
    },
    [reduced, onClose, setY],
  );

  useLayoutEffect(() => {
    if (reduced) {
      setY(0);
      return;
    }
    setY(height());
    animation.current = springTo({ from: height(), to: 0, set: setY });
    return () => {
      animation.current?.stop();
    };
    // Rises once, when it opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && close();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [close]);

  const onPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    stopAnimation();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    draggedSinceTap.current = false;
    drag.current = { grab: e.clientY - y.current, start: e.clientY, history: [[e.clientY, performance.now()]], moved: false };
  };

  const onPointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    if (!d || closing.current) return;
    if (!d.moved && Math.abs(e.clientY - d.start) < TAP_SLOP_PX) return;
    d.moved = true;
    let next = e.clientY - d.grab;
    if (next < 0) next = -rubberBand(-next, height() || 1);
    setY(next);
    d.history.push([e.clientY, performance.now()]);
    if (d.history.length > 5) d.history.shift();
  };

  const onPointerUp = () => {
    const d = drag.current;
    drag.current = null;
    if (!d || closing.current) return;
    if (!d.moved) {
      if (y.current !== 0) settle(0);
      return;
    }
    draggedSinceTap.current = true;
    const [y1, t1] = d.history[0];
    const [y2, t2] = d.history[d.history.length - 1];
    const velocity = Math.max(-MAX_FLING_PX_PER_S, Math.min(MAX_FLING_PX_PER_S, ((y2 - y1) / Math.max(16, t2 - t1)) * 1000));
    if (y.current + project(velocity) > Math.max(height() * 0.5, MIN_DISMISS_PX)) close(velocity);
    else settle(velocity);
  };

  const settle = (velocity: number) => {
    if (reduced) return setY(0);
    animation.current = springTo({ from: y.current, to: 0, velocity, damping: 0.8, response: 0.3, set: setY });
  };

  return createPortal(
    <>
      <div ref={scrimRef} data-testid="bottom-sheet-scrim" onClick={() => close()} className="fixed inset-0 z-50 bg-foreground/30" />
      <div
        ref={sheetRef}
        role="dialog"
        aria-modal="true"
        aria-label={label}
        className="fixed inset-x-0 bottom-0 z-50 max-h-[80vh] overflow-auto rounded-t-2xl border bg-popover px-[18px] pb-[max(24px,env(safe-area-inset-bottom))] text-popover-foreground shadow-2xl will-change-transform"
      >
        <div
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          className="-mx-[18px] cursor-grab touch-none px-[18px] pt-1"
        >
          <button
            type="button"
            aria-label="Drag down to close"
            onClick={() => {
              if (draggedSinceTap.current) return;
              close();
            }}
            className="relative block h-[26px] w-full"
          >
            <span aria-hidden="true" className="absolute left-1/2 top-2.5 h-[5px] w-[38px] -translate-x-1/2 rounded-full bg-border" />
          </button>
        </div>
        {children(() => close())}
      </div>
    </>,
    document.body,
  );
}
